import { collection, getDocs, query, onSnapshot, doc, CollectionReference, Query, where } from 'firebase/firestore';
import { FeaturesState, PricingProduct, ProductFeatures, ProductPrices, ProductFeature, ProductPrice, ProjectFeature, NestedFeature, ProductCoupons, ComingSoon } from "@/types/store";
import { defineStore } from 'pinia';
import { firestore } from '@/firebase/config';
import { useUserStore } from '@/store/user.store';
import { Coupon, SubscriptionInterval } from '@/types/stripe';
import { useProjectStore } from './project.store';

const defaultState: FeaturesState = {
  features: {} as Record<string, boolean>,
  live: {} as Record<string, boolean>,
  products: {} as Record<string, PricingProduct>,
  prices: {} as Record<string, ProductPrice>,
  boundItems: new Set<string>(),
  comingSoon: {} as Record<string, boolean>,
  featureProductMap: {} as Record<string, string[]>,
  unsubscribes: new Map<string, () => void>(), // Map to store unsubscribe functions
};

export const useFeaturesStore = defineStore({
  id: 'features',

  state: (): FeaturesState => ({ ...defaultState }),

  getters: {
    getFeatureById: (state) => (featureId: string) => {
      const feature: Record<string, any> = {
        enabled: state.features[featureId] ?? false,
        live: state.live[featureId] ?? false
      };
      const prefix = `${featureId}.`;

      for (const key in state.features) {
        if (key.startsWith(prefix)) {
          const subKey = key.substring(prefix.length);
          feature[subKey] = state.features[key];
        }
      }
      return feature;
    },
    getProduct: (state) => (productId: string) => {
      return state.products[productId] || null;
    },
    getPrice: (state) => (priceId: string) => {
      return state.prices[priceId] || null;
    },
    getFeaturesByProductId: (state) => {
      return (productId: string) => {
        const product = state.products[productId];
        if (!product) {
          return [];
        }

        // Helper function to recursively flatten features
        const flattenFeatures = (features: ProductFeature[]): ProductFeature[] => {
          let flattenedFeatures: ProductFeature[] = [];

          for (const feature of features) {
            if (state.featureProductMap[feature.fid]) {
              // Get nested features from featureProductMap
              const nestedFeatureIds = state.featureProductMap[feature.fid];
              // Delete the current feature and replace with nested features
              const nestedFeatures = nestedFeatureIds.map(fid => {
                // Find the corresponding feature by fid
                return Object.values(state.products)
                  .flatMap(p => Object.values(p.features))
                  .find(f => f.fid === fid) as ProductFeature;
              }).filter(f => f !== undefined); // Ensure we only add defined features

              // Recursively flatten the nested features
              flattenedFeatures = [...flattenedFeatures, ...flattenFeatures(nestedFeatures)];
            } else {
              // If no nested features, add the current feature
              flattenedFeatures.push(feature);
            }
          }

          return flattenedFeatures;
        };

        // Start by getting the initial features from the product
        const initialFeatures = Object.values(product.features);

        // Recursively flatten all features
        const allFlattenedFeatures = flattenFeatures(initialFeatures);

        return allFlattenedFeatures;
      };
    },
    getALaCartePriceByFeatureIdAndInterval: (state) => (featureId: string, interval: SubscriptionInterval) => {
      // Step 1: Find the product with metadata.id === 'a_la_carte'
      const aLaCarteProduct = Object.values(state.products).find(product => product.metadata?.id === 'a_la_carte');

      // Step 2: If the product is found, search for the price with matching featureId and interval
      if (aLaCarteProduct) {
        const matchingPrice = Object.values(aLaCarteProduct.prices).find(price =>
          price.metadata?.id === featureId && price.metadata?.interval === interval
        );

        // Step 3: Return the matching price, or null if not found
        return matchingPrice || null;
      }

      // Step 4: If no product with metadata.id === 'a_la_carte' is found, return null
      return null;
    }
  },

  actions: {
    // Fetch features via Firebase Callable Function for faster cold loads
    async loadFeaturesViaCallable(projectId: string, options?: { includeComingSoon?: boolean; stripeEnvironment?: 'production' | 'test' }) {
      if (!projectId) return;
      try {
        const { createCallableFunction } = await import('@/firebase/functions');
        const callable = await createCallableFunction('projectsOnCallGetProjectFeatures');
        const payload: any = { projectId };
        if (options && 'includeComingSoon' in options) payload.includeComingSoon = options.includeComingSoon;
        if (options && options.stripeEnvironment) payload.stripeEnvironment = options.stripeEnvironment;

        const response: any = await callable(payload);
        const result = (response?.data?.data || response?.data) as {
          features: Record<string, boolean>;
          live: Record<string, boolean>;
          comingSoon?: Record<string, boolean>;
        };

        if (!result || !result.features) {
          console.warn('[FeaturesStore] Callable returned no features; marking as bound with empty state');
          this.setState({ key: 'features', value: {} as any });
          this.setState({ key: 'live', value: {} as any });
          this.$state.boundItems.add('features');
          this.$state.boundItems.add('live');
          return;
        }

        this.setState({ key: 'features', value: result.features || {} });
        this.setState({ key: 'live', value: result.live || {} });
        this.$state.boundItems.add('features');
        this.$state.boundItems.add('live');
        // Persist to cache for fast startup on next load
        this.persistFeaturesToCache(projectId, result.features || {}, result.live || {});

        if (result.comingSoon) {
          this.setState({ key: 'comingSoon', value: result.comingSoon });
          this.$state.boundItems.add('comingSoon');
        }
      } catch (e: any) {
        const code = e?.code || e?.message || String(e);
        if (String(code).includes('permission-denied') || String(code).includes('Missing or insufficient permissions')) {
          console.warn('[FeaturesStore] Permission denied calling getProjectFeatures; marking as bound with empty state');
          try {
            this.setState({ key: 'features', value: {} as any });
            this.setState({ key: 'live', value: {} as any });
            this.$state.boundItems.add('features');
            this.$state.boundItems.add('live');
          } catch (e) {
            console.error('[FeaturesStore] Error setting empty features state:', e);
          }
        } else {
          console.error('[FeaturesStore] Error calling getProjectFeatures:', e);
          throw e;
        }
      }
    },
    // Hydrate features from local cache for faster first render on hard refresh
    loadFeaturesFromCache(projectId: string) {
      try {
        const featuresKey = `features:${projectId}`;
        const liveKey = `live:${projectId}`;
        const cachedFeaturesRaw = localStorage.getItem(featuresKey);
        const cachedLiveRaw = localStorage.getItem(liveKey);
        if (cachedFeaturesRaw && cachedLiveRaw) {
          const cachedFeatures = JSON.parse(cachedFeaturesRaw) as Record<string, boolean>;
          const cachedLive = JSON.parse(cachedLiveRaw) as Record<string, boolean>;
          // Only hydrate if cache has meaningful data
          if (cachedFeatures && Object.keys(cachedFeatures).length > 0) {
            this.setState({ key: 'features', value: cachedFeatures });
            this.setState({ key: 'live', value: cachedLive || {} });
            // Don't mark as bound yet - let bindFeaturesCollection handle that after real data loads
            // this.$state.boundItems.add('features');
            // this.$state.boundItems.add('live');
            console.log(`[FeaturesStore] Hydrated ${Object.keys(cachedFeatures).length} features from cache for project '${projectId}':`, cachedFeatures);
          } else {
            console.log(`[FeaturesStore] Cache exists but is empty for project '${projectId}'`);
          }
        } else {
          console.log(`[FeaturesStore] No cache found for project '${projectId}'`);
        }
      } catch (err) {
        console.warn('[FeaturesStore] Failed to hydrate features cache', err);
      }
    },

    // Persist features into local cache for subsequent hard refreshes
    persistFeaturesToCache(projectId: string, features: Record<string, boolean>, live: Record<string, boolean>) {
      try {
        const featuresKey = `features:${projectId}`;
        const liveKey = `live:${projectId}`;
        localStorage.setItem(featuresKey, JSON.stringify(features || {}));
        localStorage.setItem(liveKey, JSON.stringify(live || {}));
      } catch (err) {
        console.warn('[FeaturesStore] Failed to persist features cache', err);
      }
    },
    async fetchFeatures(projectId: string) {
      try {
        const t0 = Date.now();
        console.groupCollapsed(`[FeaturesStore] fetchFeatures start → projectId=${projectId}`);
        const featuresCollection = collection(firestore, `projects/${projectId}/features`);
        const q = query(featuresCollection);
        const querySnapshot = await getDocs(q);

        const features: Record<string, boolean> = {};
        const live: Record<string, boolean> = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data() as ProjectFeature;
          features[doc.id] = data.enabled || false;
          if (doc.id === 'advanced_analytics' && !features.limited_analytics) {
            features.limited_analytics = true;
          }
          live[doc.id] = data.live || false;

          // Handle flat properties
          for (const [key, value] of Object.entries(data)) {
            if (key !== 'enabled' && key !== 'live' && key !== 'nestedFeatures' && typeof value === 'boolean') {
              features[`${doc.id}.${key}`] = value;
            }
          }

          if ('nestedFeatures' in data) {
            for (const [key, value] of Object.entries((data.nestedFeatures) as NestedFeature) as any) {
              features[`${doc.id}.${key}`] = value.enabled || false;
              live[`${doc.id}.${key}`] = value.live || false;
            }
          }
        });
        console.log(`[FeaturesStore] Fetched ${Object.keys(features).length} features from Firestore for project '${projectId}' in ${Date.now()-t0}ms`);
        this.setState({ key: 'features', value: features });
        this.setState({ key: 'live', value: live });
        this.$state.boundItems.add('features');
        this.$state.boundItems.add('live');
        // Persist to cache for fast startup on next load
        this.persistFeaturesToCache(projectId, features, live);
        console.groupEnd();
      } catch (error: any) {
        const msg = error?.message || String(error);
        console.error('[FeaturesStore] Error fetching features:', error);
        // Fallback to callable on permission denied to avoid empty UI in private label dev
        if (msg.includes('permission-denied') || msg.includes('Missing or insufficient permissions')) {
          try {
            await this.loadFeaturesViaCallable(projectId);
          } catch (e) {
            console.warn('[FeaturesStore] Callable fallback also failed:', e);
          }
        }
      }
    },

    async fetchComingSoon(active: boolean): Promise<ComingSoon> {
      const comingSoon: ComingSoon = {};

      try {
        // Query only the products where active equals the provided parameter.
        const productsCollection = collection(firestore, 'products');
        const productsQuery = query(productsCollection, where('active', '==', active));
        const productsSnapshot = await getDocs(productsQuery);

        // Create an array of promises for fetching features in parallel.
        const featuresPromises = productsSnapshot.docs.map(async (doc) => {
          const productId = doc.id;
          const featuresCollection = collection(firestore, `products/${productId}/features`);
          const featuresSnapshot = await getDocs(featuresCollection);

          featuresSnapshot.forEach((featureDoc) => {
            const featureData = featureDoc.data() as ProductFeature;
            comingSoon[featureData.fid] = featureData.coming_soon || false;
          });
        });

        // Wait until all feature subcollection queries are complete.
        await Promise.all(featuresPromises);
      } catch (error) {
        console.error('Error fetching coming soon features:', error);
      }

      // Update the store state as before.
      this.setState({ key: 'comingSoon', value: comingSoon });
      this.$state.boundItems.add('comingSoon');

      return comingSoon;
    },

    async fetchProducts(active: boolean) {
      try {
        // Fetch comingSoon data quickly if not already bound
        let comingSoon: ComingSoon = {};
        if (!this.boundItems.has('comingSoon')) {
          const comingSoonPromise = this.fetchComingSoon(active);
          comingSoon = await comingSoonPromise;
          this.setState({ key: 'comingSoon', value: comingSoon });
          this.$state.boundItems.add('comingSoon');
        }

        const productsCollection = collection(firestore, `products`);
        const q = query(productsCollection);
        const querySnapshot = await getDocs(q);

        const products: Record<string, PricingProduct> = {};
        const allPrices: Record<string, ProductPrice> = {}; // Directory of all prices
        const featureProductMap: Record<string, string[]> = {}; // Map of stripe_metadata_id to featureIds

        for (const doc of querySnapshot.docs) {
          const productData = doc.data() as PricingProduct;
          const productId = doc.id;
          const stripe_metadata_id = productData.stripe_metadata_id;

          if (productData.active !== active) {
            continue;
          }

          const featuresCollection = collection(firestore, `products/${productId}/features`);
          const featuresSnapshot = await getDocs(featuresCollection);
          const features: ProductFeatures = {};
          const featureIds: string[] = []; // Array to store featureIds

          featuresSnapshot.forEach((featureDoc) => {
            const featureData = featureDoc.data() as ProductFeature;
            featureData.id = featureDoc.id;
            features[featureDoc.id] = featureData;
            featureIds.push(featureData.fid); // Collect featureIds here
          });

          const sortedFeatures = Object.values(features).sort((a, b) => a.position - b.position);
          const orderedFeatures: ProductFeatures = {};

          sortedFeatures.forEach(feature => {
            orderedFeatures[feature.id] = feature;
          });

          const pricesCollection = collection(firestore, `products/${productId}/prices`);
          const pricesSnapshot = await getDocs(pricesCollection);
          const prices: ProductPrices = {};
          pricesSnapshot.forEach((priceDoc) => {
            const priceData = priceDoc.data() as ProductPrice;
            priceData.id = priceDoc.id;
            prices[priceDoc.id] = priceData;
            allPrices[priceDoc.id] = priceData; // Add to the directory
          });

          const couponsCollection = collection(firestore, `products/${productId}/coupons`);
          const couponsSnapshot = await getDocs(couponsCollection);
          const coupons: ProductCoupons = {};
          couponsSnapshot.forEach((couponDoc) => {
            const couponData = couponDoc.data() as Coupon;
            couponData.id = couponDoc.id;
            coupons[couponDoc.id] = couponData;
          });

          featureProductMap[stripe_metadata_id] = featureIds; // Map featureIds to stripe_metadata_id

          products[productId] = {
            ...productData,
            id: productId,
            features: orderedFeatures,
            prices,
            coupons
          };
        }

        const sortedProducts = Object.values(products).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const orderedProducts: Record<string, PricingProduct> = {};
        sortedProducts.forEach(product => {
          orderedProducts[product.id] = product;
        });

        // Set state with fetched data
        this.setState({ key: 'products', value: orderedProducts });
        this.setState({ key: 'prices', value: allPrices }); // Set the price directory in the state
        this.setState({ key: 'featureProductMap', value: featureProductMap }); // Set the featureProductMap in the state

        // Update boundItems
        this.$state.boundItems.add('products');
        this.$state.boundItems.add('prices');
        this.$state.boundItems.add('featureProductMap');
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    },

    setState<K extends keyof FeaturesState>(payload: { key: K; value: FeaturesState[K] }) {
      this.$patch(state => {
        state[payload.key] = payload.value;
      });
    },

    reset<K extends keyof FeaturesState>(property?: K) {
      if (property) {
        // Unbind the specific property if it's currently bound
        const unsubscribe = this.$state.unsubscribes.get(property as string) as (() => void) | undefined;
        if (unsubscribe) {
          unsubscribe(); // Call the unsubscribe function to stop listening
          this.$state.unsubscribes.delete(property as string); // Remove from unsubscribes map
        }
        this.$state.boundItems.delete(property as string); // Remove from boundItems set

        // Reset the specific property to its default value
        this.$state[property] = defaultState[property];
        localStorage.removeItem(property as string);

      } else {
        // Reset all state properties and unbind all listeners
        this.$state.unsubscribes.forEach((unsubscribe: () => void) => {
          unsubscribe(); // Unsubscribe from Firestore listeners
        });

        // Clear all bound items and unsubscribes
        this.$state.boundItems.clear();
        this.$state.unsubscribes.clear();

        // Reset the entire state
        Object.assign(this.$state, defaultState);
        Object.keys(defaultState).forEach((key) => {
          localStorage.removeItem(key);
        });
      }
    },

    // New action to bind a collection
    async bindFeaturesCollection(projectId: string) {
      const userStore = useUserStore();
      let authUser = userStore.authUser as any;
      if (!authUser || !authUser.uid) {
        try {
          const { Auth } = await import('@/firebase/auth');
          if (Auth?.currentUser?.uid) {
            authUser = Auth.currentUser;
          }
        } catch (error) {
          console.error(`Error importing Auth:`, error);
        }
      }

      if (!authUser || !authUser.uid) {
        console.warn(`No authenticated user (yet). Deferring features binding.`);
        return;
      }

      // First, hydrate from cache to avoid a cold start gap during hard refresh
      this.loadFeaturesFromCache(projectId);

      const boundKey = `features`;
      // Check if we have an active listener (unsubscribe function) rather than just boundItems
      // This prevents skipping the real data fetch when we only have cached data
      if (this.$state.unsubscribes.has(boundKey)) {
        console.log(`Features collection is already bound with active listener.`);
        return;
      }

      const featuresCollection: CollectionReference | Query = collection(firestore, `projects/${projectId}/features`);

      await this.fetchFeatures(projectId);

      const unsubscribe = onSnapshot(featuresCollection, (snapshot) => {
        const t0 = Date.now();
        const features: Record<string, boolean> = {};
        const live: Record<string, boolean> = {};

        snapshot.forEach((doc) => {
          const data = doc.data() as ProjectFeature;
          features[doc.id] = data.enabled || false;
          live[doc.id] = data.live || false;

          // Handle flat properties
          for (const [key, value] of Object.entries(data)) {
            if (key !== 'enabled' && key !== 'live' && key !== 'nestedFeatures' && typeof value === 'boolean') {
              features[`${doc.id}.${key}`] = value;
            }
          }

          if ('nestedFeatures' in data) {
            for (const [key, value] of Object.entries(data.nestedFeatures) as any) {
              features[`${doc.id}.${key}`] = value.enabled || false;
              live[`${doc.id}.${key}`] = value.live || false;
            }
          }
        });

        const specificFeatures = ['mail', 'channels', 'events', 'address_book'];
        const anySpecificFeatureEnabled = specificFeatures.some(feature => features[feature]);

        features['portal'] = anySpecificFeatureEnabled;

        if (!anySpecificFeatureEnabled) {
          features['portal'] = false;
        }

        this.setState({ key: 'features', value: features });
        this.setState({ key: 'live', value: live });
        this.$state.boundItems.add(boundKey);
        // Update cache as live snapshot streams in
        this.persistFeaturesToCache(projectId, features, live);
        console.log(`[FeaturesStore] onSnapshot applied ${Object.keys(features).length} features for '${projectId}' in ${Date.now()-t0}ms`);
      }, (error: any) => {
        console.error(`Error binding features collection:`, error);
        const msg = error?.message || String(error);
        // On permission issues, mark as bound with current cache and retry later
        if (msg.includes('permission-denied') || msg.includes('Missing or insufficient permissions')) {
          try {
            this.$state.boundItems.add('features');
            this.$state.boundItems.add('live');
          } catch (e) {
            console.warn('[FeaturesStore] Failed to mark bound after permission error:', e);
          }
          // Schedule a delayed rebind attempt; claims may update shortly after login
          setTimeout(() => {
            this.$state.unsubscribes.delete('features');
            this.bindFeaturesCollection(projectId).catch(() => {});
          }, 5000);
        }
      });

      this.$state.unsubscribes.set(boundKey, unsubscribe);
    },

    // New action to bind a document
    async bindFeaturesDocument(projectId: string, featureId: string) {
      const userStore = useUserStore();
      let authUser = userStore.authUser as any;
      if (!authUser || !authUser.uid) {
        try {
          const { Auth } = await import('@/firebase/auth');
          if (Auth?.currentUser?.uid) {
            authUser = Auth.currentUser;
          }
        } catch (error) {
          console.error(`Error importing Auth:`, error);
        }
      }

      if (!authUser || !authUser.uid) {
        console.warn(`No authenticated user (yet). Deferring features document binding.`);
        return;
      }

      const boundKey = `features_${featureId}`;
      if (this.$state.boundItems.has(boundKey)) {
        console.log(`Features document is already bound.`);
        return;
      }

      const docRef = doc(firestore, `projects/${projectId}/features`, featureId);

      const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as ProjectFeature;
          const features = { ...this.$state.features };
          const live = { ...this.$state.live };

          features[featureId] = data.enabled || false;
          live[featureId] = data.live || false;

          // Handle flat properties
          for (const [key, value] of Object.entries(data)) {
            if (key !== 'enabled' && key !== 'live' && key !== 'nestedFeatures' && typeof value === 'boolean') {
              features[`${featureId}.${key}`] = value;
            }
          }

          if ('nestedFeatures' in data) {
            for (const [key, value] of Object.entries(data.nestedFeatures) as any) {
              features[`${featureId}.${key}`] = value.enabled || false;
              live[`${featureId}.${key}`] = value.live || false;
            }
          }

          this.setState({ key: 'features', value: features });
          this.setState({ key: 'live', value: live });
          this.$state.boundItems.add(boundKey);
        } else {
          console.log(`Document at path projects/${projectId}/features/${featureId} does not exist.`);
        }
      }, (error) => {
        console.error(`Error binding features document:`, error);
      });

      this.$state.unsubscribes.set(boundKey, unsubscribe);
    },

    // New action to unbind collection or document
    unbind(boundKey: keyof FeaturesState) {
      if (this.$state.boundItems.has(boundKey)) {
        const unsubscribe = this.$state.unsubscribes.get(boundKey as string);
        if (unsubscribe) {
          unsubscribe(); // Call the unsubscribe function to stop listening to Firestore changes
          this.$state.unsubscribes.delete(boundKey as string); // Remove the unsubscribe function from the map
        }
        this.$state.boundItems.delete(boundKey); // Remove the bound item from the set
        this.setState({ key: boundKey, value: defaultState[boundKey] }); // Reset the state
        console.log(`Unbound ${boundKey} successfully.`);
      } else {
        console.log(`${boundKey} is not currently bound.`);
      }
    },
    subscriptionHasFeature(fid: string) {
      const projectStore = useProjectStore();
      const subscriptions = projectStore.project_subscriptions;

      if (!subscriptions || !subscriptions.length || !subscriptions[0]?.features?.current) {
        return false;
      }

      return subscriptions[0].features.current.includes(fid);
    },

    // Helper method to get hierarchical payment features status
    getPaymentFeatures() {
      return {
        payments: {
          enabled: this.features.payments || false,
          live: this.live.payments || false,
          subFeatures: {
            donations: {
              enabled: this.features['payments.donations'] || false,
              live: this.live['payments.donations'] || false
            },
            marketplace: {
              enabled: this.features['payments.marketplace'] || false,
              live: this.live['payments.marketplace'] || false
            }
          }
        }
      };
    },

    // Helper method to check if any payment sub-features are enabled
    hasEnabledPaymentSubFeatures() {
      return this.features['payments.donations'] || this.features['payments.marketplace'];
    }
  },
});
